import React, { useRef } from 'react'
import { useTransition, animated, SpringValue } from 'react-spring'

import { useToggle, useDisableOverflow } from '@insquad/tools'

import { CloseIcon } from 'src/common/components/icons/CloseIcon'
import { Resume } from 'src/graphql/types'

import { ResumeDrawerContent } from './ResumeDrawerContent/ResumeDrawerContent'

import s from './ResumesDrawer.module.scss'

export type ResumesDrawerProps = {
  isOpen: boolean
  resume: Resume | null
  onClose: () => void
}

export const ResumesDrawer: React.FC<ResumesDrawerProps> = ({
  isOpen,
  resume,
  onClose,
}) => {
  const drawerRef = useRef<HTMLDivElement | null>(null)

  const hintModalToggler = useToggle()

  const slideTransition = useTransition(isOpen, {
    from: {
      transform: 'translateX(100%)',
    },
    enter: {
      transform: 'translateX(0%)',
    },
    leave: {
      transform: 'translateX(100%)',
    },
    config: {
      duration: 280,
    },
  })

  const fadeTransition = useTransition(isOpen, {
    from: { opacity: 0, visibility: 'hidden' },
    enter: {
      opacity: 1,
      visibility: 'visible',
    },
    leave: {
      opacity: 0,
      visibility: 'hidden',
    },
    config: {
      duration: 240,
    },
  })

  useDisableOverflow(isOpen)

  return (
    <>
      {fadeTransition(
        (style, isVisible) =>
          isVisible && (
            <animated.div
              style={style as unknown as Record<string, SpringValue<string>>}
              className={s.ResumesDrawer__blackout}
              onClick={
                hintModalToggler.value ? hintModalToggler.unset : onClose
              }
            />
          )
      )}

      {slideTransition(
        (style, isVisible) =>
          isVisible && (
            <>
              <animated.aside
                ref={drawerRef}
                style={style}
                className={s.ResumesDrawer__drawer}
              >
                <div onClick={onClose} className={s.ResumesDrawer__close}>
                  <CloseIcon />
                </div>

                <div className={s.ResumesDrawer__inner}>
                  {resume && (
                    <ResumeDrawerContent
                      drawerElement={drawerRef.current}
                      resume={resume}
                      hintModalToggler={hintModalToggler}
                      onClose={onClose}
                    />
                  )}
                </div>
              </animated.aside>
            </>
          )
      )}
    </>
  )
}
